// Copyright 2019 Sorama B.V.

import { BindingEngine, ObserverLocator } from "aurelia-binding";
import { autoinject, transient } from "aurelia-framework";
import { ParserCatalog } from "parsers/ParserCatalog";
import { IUserSettingsService } from "services/UserManagement/IUserSettingsService";
import { IWorkflowModuleReference } from "../Interfaces/IWorkflowModule";
import { WorkflowPropertyScope } from "../Interfaces/IWorkflowPropertyMetadata";
import { ArrayExtensions } from "./../../../extensions/ArrayExtensions";
import { LocalStoredDefaultValue } from "./../Interfaces/IWorkflowPropertyMetadata";
import { ModuleSetScopeObserver, ScopedWorkflowProperty } from "./ModuleSetScopeObserver";
import { getAllWorkflowPropertyMetadata, getWorkflowPropertyMetadata } from "./WorkflowPropertyAttribute";
import { WorkflowPropertyObserver } from "./WorkflowPropertyObserver";
import { WorkflowPropertyBase, WorkflowUriProperty } from "./WorkflowPropertyUriConverter";

export class WorkflowProperty extends WorkflowUriProperty {
    public objectName?: string;
}

@autoinject()
@transient()
export class WorkflowPropertyManager {
    public get workflowProperties(): WorkflowProperty[] {
        return this.workflowPropertyObservers
            .map((x) => {
                return <WorkflowProperty>{
                    name: x.propertyName,
                    value: x.value,
                    objectName: x.objectType,
                    scopeIdxs: x.workflowModuleTargetIds,
                };
            })
            .concat(...this.nonUsedWorkflowProperties);
    }

    private get workflowPropertyObservers(): WorkflowPropertyObserver[] {
        const result: WorkflowPropertyObserver[] = [];
        for (const prop of this._scopedWorkflowProperties) {
            result.push(...prop.workflowPropertyObservers);
        }
        return this._workflowProperties.concat(result);
    }

    private _workflowProperties: WorkflowPropertyObserver[] = [];
    private _scopedWorkflowProperties: ModuleSetScopeObserver[] = [];

    private subscribers: ((properties: WorkflowUriProperty[]) => void)[] = [];

    /**
     * Properties that are in the URI, but not used (yet) by modules.
     */
    private nonUsedWorkflowProperties: WorkflowUriProperty[] = [];

    constructor(
        private bindingEngine: BindingEngine,
        private locator: ObserverLocator,
        private userSetting: IUserSettingsService = undefined
    ) {}

    public async watchModule(workflowModule: IWorkflowModuleReference): Promise<void> {
        // There is no module, so do nothing
        if (!workflowModule) return Promise.resolve();

        // Loop over all workflow properties in a module
        const properties = getAllWorkflowPropertyMetadata(workflowModule);

        const observerPromises: Promise<void>[] = [];

        for (const propertyName in properties) {
            const propertyMetadata = getWorkflowPropertyMetadata(workflowModule, propertyName);
            const binding = propertyMetadata.bindingMode;
            const parser = ParserCatalog.getParser(propertyMetadata.objectType);

            if (
                this.workflowPropertyObservers.findIndex(
                    (x) =>
                        x.propertyName == propertyName &&
                        x.getModules().findIndex((y) => y.moduleId == workflowModule.moduleId) !== -1
                ) !== -1
            ) {
                throw new Error(
                    `Requested property (${propertyName}) in module (${workflowModule.moduleName}) is already observed.`
                );
            }

            const promise = this.getDefaultValue(workflowModule, propertyName).then((defaultValue) => {
                // Determine scope of the property
                switch (propertyMetadata.scope) {
                    case WorkflowPropertyScope.workflowModule: {
                        const existingObserver = this.workflowPropertyObservers.find(
                            (x) =>
                                x.propertyName == propertyName &&
                                x.workflowModuleTargetIds &&
                                x.workflowModuleTargetIds.findIndex((y) => y == workflowModule.moduleId) !== -1
                        );

                        if (existingObserver && existingObserver.workflowModuleTargetIds.length > 1) {
                            throw new Error(
                                "A workflowModule scope cannot be added as a target in a workflowModuleSet."
                            );
                        }

                        // Scope is unique per module, so always create a new observer
                        const scopedWorkflowProperty = new WorkflowPropertyObserver(
                            this.bindingEngine,
                            propertyName,
                            propertyMetadata.objectType,
                            this.locator,
                            [workflowModule.moduleId],
                            defaultValue,
                            parser
                        );
                        scopedWorkflowProperty.addModule(workflowModule, binding);
                        scopedWorkflowProperty.subscribe(() => {
                            this.workflowPropertiesChanged();
                        });
                        this._workflowProperties.push(scopedWorkflowProperty);
                        break;
                    }
                    case WorkflowPropertyScope.workflowModuleSet: {
                        if (
                            this.workflowPropertyObservers.find(
                                (x) =>
                                    x.propertyName == propertyName &&
                                    x.workflowModuleTargetIds &&
                                    x.workflowModuleTargetIds.findIndex((y) => y == workflowModule.moduleId) !== -1
                            )
                        ) {
                            throw new Error(
                                "A scoped workflowModuleSet cannot be added as a target in another scoped workflowModuleSet."
                            );
                        }

                        const moduleSetObserver = new ModuleSetScopeObserver(
                            this.bindingEngine,
                            workflowModule,
                            propertyName,
                            binding,
                            propertyMetadata.objectType,
                            this.locator,
                            defaultValue
                        );

                        // Check if the property is in the "unused" list and process it (e.g. defined in URL and refreshing)
                        const unusedProps = this.nonUsedWorkflowProperties.filter(
                            (x) =>
                                x.name == propertyName &&
                                x.scopeIdxs &&
                                x.scopeIdxs.findIndex((y) => y == workflowModule.moduleId) >= 0
                        );
                        for (const prop of unusedProps) {
                            if (prop.scopeIdxs.length == 2) {
                                const propValue: ScopedWorkflowProperty<unknown> = workflowModule[propertyName];
                                const targetIdx = prop.scopeIdxs.find((x) => x != workflowModule.moduleId);
                                propValue.add(targetIdx, prop.value);
                                this.nonUsedWorkflowProperties.splice(
                                    this.nonUsedWorkflowProperties.findIndex((x) => x == prop),
                                    1
                                );
                            }
                        }

                        moduleSetObserver.subscribe(() => {
                            this.checkModuleSetObserver();
                            this.workflowPropertiesChanged();
                        });

                        this._scopedWorkflowProperties.push(moduleSetObserver);
                        break;
                    }
                    case WorkflowPropertyScope.workflow:
                    default: {
                        // Check if ownership is claimed by a ModuleSet scope property
                        const moduleSet = this._scopedWorkflowProperties.find((x) => x.propertyName == propertyName);

                        if (moduleSet) {
                            moduleSet.addModule(workflowModule);
                            break;
                        }

                        // Lets check the rest of the options
                        let scopedWorkflowProperty = this.workflowPropertyObservers.find(
                            (x) =>
                                x.propertyName == propertyName &&
                                (!x.workflowModuleTargetIds ||
                                    x.workflowModuleTargetIds.findIndex((y) => y == workflowModule.moduleId) !== -1)
                        );

                        if (scopedWorkflowProperty) {
                            if (scopedWorkflowProperty.objectType !== propertyMetadata.objectType) {
                                console.warn(
                                    "WorkflowPropertyObserver: observed property " +
                                        propertyName +
                                        " has inconsistent property type name. "
                                );
                            }

                            scopedWorkflowProperty.addModule(workflowModule, binding);
                            break;
                        }

                        // Check non used variables
                        const nonUsedProperty = this.nonUsedWorkflowProperties.find(
                            (x) =>
                                x.name == propertyName &&
                                (x.scopeIdxs == undefined ||
                                    x.scopeIdxs.findIndex((y) => y == workflowModule.moduleId) !== -1)
                        );

                        let targetIds: number[];

                        if (nonUsedProperty) {
                            targetIds = nonUsedProperty.scopeIdxs;
                        }

                        scopedWorkflowProperty = new WorkflowPropertyObserver(
                            this.bindingEngine,
                            propertyName,
                            propertyMetadata.objectType,
                            this.locator,
                            targetIds,
                            defaultValue,
                            parser
                        );
                        scopedWorkflowProperty.addModule(workflowModule, binding);
                        scopedWorkflowProperty.subscribe(() => {
                            this.workflowPropertiesChanged();
                        });
                        this._workflowProperties.push(scopedWorkflowProperty);
                        break;
                    }
                }
            });

            observerPromises.push(promise);
        }

        return Promise.all(observerPromises).then(() => {
            this.syncNonWorkflowProperties();
            this.checkModuleSetObserver();
        });
    }

    public restoreState(
        workflowModule: IWorkflowModuleReference,
        properties: WorkflowPropertyBase[],
        forceStateScope = true
    ): Promise<void> {
        if (forceStateScope) {
            for (const prop of properties) {
                // Make sure that this property is not monitored anymoe by other observers
                this.workflowPropertyObservers
                    .filter((x) => x.propertyName == prop.name)
                    .forEach((x) => x.removeModule(workflowModule));

                // create a new one
                const propertyMetadata = getWorkflowPropertyMetadata(workflowModule, prop.name);
                if (propertyMetadata == undefined) {
                    console.warn("Unable to find property metadata for " + prop.name);
                    continue;
                }

                const parser = ParserCatalog.getParser(propertyMetadata.objectType);

                const scopedWorkflowProperty = new WorkflowPropertyObserver(
                    this.bindingEngine,
                    prop.name,
                    propertyMetadata.objectType,
                    this.locator,
                    [workflowModule.moduleId],
                    prop.value,
                    parser
                );
                scopedWorkflowProperty.addModule(workflowModule, propertyMetadata.bindingMode);
                scopedWorkflowProperty.subscribe(() => {
                    this.workflowPropertiesChanged();
                });
                this._workflowProperties.push(scopedWorkflowProperty);
            }

            return Promise.resolve();
        }

        const props: WorkflowUriProperty[] = [];
        for (const prop of properties) {
            const scopedProp = this._scopedWorkflowProperties.find((x) => x.propertyName == prop.name);

            if (scopedProp && !scopedProp.targetModuleIds.find((x) => x == workflowModule.moduleId)) {
                scopedProp.addModule(workflowModule);
                scopedProp.workflowPropertyObservers.find((x) => x.propertyName == prop.name).value = prop.value;
                continue;
            } else if (scopedProp && scopedProp.targetModuleIds.find((x) => x == workflowModule.moduleId)) {
                const propObserver = scopedProp.workflowPropertyObservers.find(
                    (x) => x.getModuleById(workflowModule.moduleId) != undefined
                );
                if (propObserver) {
                    propObserver.value = prop.value;
                    continue;
                }
            }

            const uriProp = prop as WorkflowUriProperty;
            uriProp.scopeIdxs = undefined;
            props.push(uriProp);
        }

        this.updateWorkflowProperties(props);

        return Promise.resolve().then(() => {
            this.workflowPropertiesChanged();
        });
    }

    public removeModule(module: IWorkflowModuleReference): void {
        if (this._workflowProperties) {
            this._workflowProperties.forEach((x) => {
                if (x.getModuleById(module.moduleId)) x.removeModuleById(module.moduleId);
            });
        }

        if (this._scopedWorkflowProperties) {
            this._scopedWorkflowProperties.forEach((x) => {
                x.removeModule(module.moduleId);
            });
        }
    }

    public updateWorkflowProperties(properties: WorkflowUriProperty[]): void {
        for (const property of properties) {
            const propertyObservers = this.workflowPropertyObservers.filter((x) => x.propertyName == property.name);

            // Check if we have any candidates
            if (propertyObservers.length > 0) {
                const observer = propertyObservers.find((x) =>
                    ArrayExtensions.equals(x.workflowModuleTargetIds, property.scopeIdxs)
                );

                // We found an existing property with the exact same scope, just assign the value
                if (observer) {
                    observer.value = property.value;
                    continue;
                }
            }

            const nonUsedProperty = this.nonUsedWorkflowProperties.find(
                (x) => x.name == property.name && ArrayExtensions.equals(x.scopeIdxs, property.scopeIdxs)
            );
            if (nonUsedProperty) {
                nonUsedProperty.value = property.value;
                continue;
            }

            // Non existing yet, so we need to remember it for the future
            if (
                !this.nonUsedWorkflowProperties.find(
                    (x) =>
                        x.name === property.name &&
                        (property.scopeIdxs == undefined ||
                            x.scopeIdxs == undefined ||
                            property.scopeIdxs == x.scopeIdxs ||
                            new Set(property.scopeIdxs.concat(...x.scopeIdxs)).size == property.scopeIdxs.length)
                )
            ) {
                this.nonUsedWorkflowProperties.push(property);
            }
        }
    }

    public subscribeWorkflowPropertyChanged(subscriber: (properties: WorkflowUriProperty[]) => void): void {
        this.subscribers.push(subscriber);
    }

    public dispose(): void {
        this.clearProperties();
        this._workflowProperties = undefined;
        this._scopedWorkflowProperties = undefined;
    }

    public clearProperties(): void {
        if (this._workflowProperties) {
            while (this._workflowProperties.length > 0) {
                this._workflowProperties.pop().dispose();
            }
        }

        if (this._scopedWorkflowProperties) {
            while (this._scopedWorkflowProperties.length > 0) {
                this._scopedWorkflowProperties.pop().dispose();
            }
        }
    }

    private workflowPropertiesChanged(): void {
        if (this.subscribers) {
            for (const subscriber of this.subscribers) {
                subscriber(this.workflowProperties);
            }
        }
    }

    private syncNonWorkflowProperties() {
        this.nonUsedWorkflowProperties = this.nonUsedWorkflowProperties.filter((prop) => {
            for (const workflowProp of this.workflowPropertyObservers.filter((x) => x.propertyName == prop.name)) {
                if (
                    prop.scopeIdxs == workflowProp.workflowModuleTargetIds || // Workflow scope
                    (prop.scopeIdxs &&
                        workflowProp.workflowModuleTargetIds &&
                        new Set(prop.scopeIdxs.concat(...workflowProp.workflowModuleTargetIds)).size ==
                            prop.scopeIdxs.length)
                ) {
                    // Module & ModuleSet Scope
                    const parser = ParserCatalog.getParser(workflowProp.objectType);
                    if (parser) {
                        if (Array.isArray(prop.value)) {
                            workflowProp.value = [];
                            for (const val of prop.value) {
                                (workflowProp.value as unknown[]).push(
                                    typeof val == "string" ? parser.fromWorkflowProperty(val) : val
                                );
                            }
                        } else if (typeof prop.value == "string" || typeof prop.value == "number") {
                            workflowProp.value = parser.fromWorkflowProperty(prop.value as string);
                        } else {
                            // We have some sort of object?
                            throw new Error(
                                "Unsupported object type in workflow property, not a number, string, or array."
                            );
                        }
                    } else {
                        workflowProp.value = prop.value;
                    }
                    return false;
                }
            }

            return true;
        });
    }

    private async getDefaultValue(workflowModule: IWorkflowModuleReference, propertyName: string): Promise<unknown> {
        const propertyMetadata = getWorkflowPropertyMetadata(workflowModule, propertyName);

        if (propertyMetadata.defaultValue && propertyMetadata.defaultValue.key) {
            const defaultValue: LocalStoredDefaultValue = propertyMetadata.defaultValue;
            return this.userSetting
                .get(defaultValue.group ? defaultValue.group : workflowModule.moduleName, defaultValue.key)
                .catch(() => {
                    // Problems retrieving value (e.g. never stored). Use the default value.
                    return defaultValue.defaultValue;
                });
        } else {
            return Promise.resolve(propertyMetadata.defaultValue);
        }
    }

    private checkModuleSetObserver(): void {
        for (const moduleSetObserver of this._scopedWorkflowProperties) {
            const ids = moduleSetObserver.targetModuleIds.filter(
                (x) => moduleSetObserver.workflowPropertyObservers.findIndex((y) => !!y.getModuleById(x)) == -1
            );

            for (const id of ids) {
                const propIdx = this._workflowProperties.findIndex(
                    (x) => x.propertyName == moduleSetObserver.propertyName && !!x.getModuleById(id)
                );
                if (propIdx != -1) {
                    const mod = this._workflowProperties[propIdx].getModuleById(id);
                    this._workflowProperties[propIdx].removeModuleById(id);

                    if (this._workflowProperties[propIdx].getModules().length == 0) {
                        this._workflowProperties[propIdx].dispose();
                        this._workflowProperties.splice(propIdx, 1);
                    }

                    moduleSetObserver.addModule(mod);
                }
            }
        }
    }
}
